// Local mock data for development without VPN/office network access.
// Enable with ?mock=1 in the URL.

const now = Math.floor(Date.now() / 1000);

const NAMES = [
    'anna.andersson',
    'erik.eriksson',
    'maria.svensson',
    'johan.johansson',
    'karin.nilsson',
    'lars.larsson',
    'eva.olofsson',
    'per.persson',
    'lena.gustafsson',
    'anders.karlsson',
    'sara.nilsson',
    'mikael.pettersson',
    'asa.jonsson',
    'fredrik.hansson',
    'linda.berg',
    'daniel.lindqvist',
    'emma.lindberg',
    'mattias.lundberg',
    'jenny.lind',
    'peter.holm',
    'malin.berglund',
    'thomas.nystrom',
    'sofia.axelsson',
    'niklas.mattsson',
    'helena.forsberg',
    'marcus.akesson',
    'ida.danielsson',
    'oskar.lundgren',
    'elin.hakansson',
    'simon.isaksson',
];

// Irregular headcount per floor (sums to NAMES.length) so "Våningsplan" sort
// produces uneven row lengths instead of a neat 5-per-floor grid.
const PEOPLE_PER_FLOOR = [8, 3, 6, 2, 7, 4];

// Real room numbers per floor, taken from area-v{floor}.json's "rooms"
// fields — this is the same format real Unicator data uses for
// ap.location ("101", "205", ...), so mock people land on an actual room
// polygon on the floor-plan map instead of nowhere.
const REAL_ROOM_NUMBERS_BY_FLOOR = {
    1: ['101', '102', '103', '104', '106', '107', '109', '110', '111'],
    2: ['201', '202', '203', '204', '205', '207', '209', '210', '212', '213'],
    3: ['302', '303', '305', '306', '308', '310', '315', '317'],
    4: ['403', '404', '406', '407', '410', '417', '418'],
    5: ['501', '502', '504', '505', '506', '508', '510', '512'],
    6: ['601', '602', '605', '606', '608', '609', '610'],
};

const FLOORS = PEOPLE_PER_FLOOR.reduce((assignments, count, floorIndex) => {
    const floor = floorIndex + 1;
    const roomNumbers = REAL_ROOM_NUMBERS_BY_FLOOR[floor];

    for (let i = 0; i < count; i++) {
        assignments.push({ floor, roomNumber: roomNumbers[i % roomNumbers.length] });
    }

    return assignments;
}, []);

// The real room names/numbers, as given — floor is the room number's first digit.
const ROOMS = [
    { name: 'Skogen', number: '101' },
    { name: 'Enen', number: '112' },
    { name: 'Rosen', number: '211' },
    { name: 'Dahlian', number: '213' },
    { name: 'Aladdin', number: '314' },
    { name: 'Bambi', number: '315' },
    { name: 'Doppio', number: '601' },
    { name: 'Espresso', number: '604' },
    { name: 'Lungo', number: '605' },
    { name: 'Ristretto', number: '606' },
    { name: 'Latte', number: '607' },
    { name: 'Americano', number: '608' },
    { name: 'Mocca', number: '612' },
];

const roomIdentifier = (room) => {
    return `room-floor${room.number[0]}-stendahls-se-${room.number}`;
};

const timeToday = (hours, minutes) => {
    return new Date(new Date().setHours(hours, minutes, 0, 0)).toISOString();
};

// Each room's booked slots today, as given — [startHours, startMinutes, endHours, endMinutes].
const ROOM_BOOKINGS = {
    Skogen: [
        [14, 0, 14, 30],
        [15, 30, 16, 0],
        [9, 30, 10, 0],
    ],
    Enen: [
        [13, 0, 14, 0],
        [14, 0, 16, 0],
        [11, 0, 12, 0],
    ],
    Rosen: [
        [14, 0, 17, 0],
    ],
    Dahlian: [
        [9, 30, 10, 0],
        [11, 0, 12, 0],
        [10, 0, 10, 30],
        [14, 0, 14, 30],
    ],
    Aladdin: [
        [11, 0, 11, 30],
        [11, 30, 12, 0],
        [12, 30, 13, 0],
        [15, 30, 16, 0],
        [10, 0, 10, 30],
        [9, 30, 10, 0],
    ],
    Bambi: [
        [11, 30, 12, 15],
        [13, 30, 14, 0],
        [14, 0, 14, 30],
        [9, 30, 9, 50],
        [14, 30, 14, 45],
        [13, 0, 13, 30],
    ],
    Doppio: [
        [10, 0, 12, 0],
        [13, 0, 14, 0],
        [14, 0, 15, 30],
    ],
    Espresso: [
        [9, 25, 10, 30],
        [13, 25, 14, 5],
    ],
    Lungo: [
        [9, 30, 17, 0],
    ],
    Ristretto: [
        [9, 0, 10, 30],
        [15, 30, 16, 15],
        [13, 30, 14, 0],
    ],
    Latte: [
        [15, 30, 16, 15],
    ],
    Americano: [
        [11, 30, 12, 0],
    ],
    Mocca: [],
};

// A handful of people are currently sitting inside a booked meeting room
// (not just near it) — their WiFi access point location is set to that
// room's real number, which is how Rooms.jsx matches them to a room row
// (same mechanism used for real Unicator data, not a mock-only shortcut).
const MEETING_ROOM_OCCUPANT_LOCATIONS = {
    'anna.andersson': ROOMS[4].number, // Aladdin
    'erik.eriksson': ROOMS[4].number, // Aladdin
    'maria.svensson': ROOMS[2].number, // Rosen
    'karin.nilsson': ROOMS[6].number, // Doppio
    'lena.gustafsson': ROOMS[6].number, // Doppio
    'anders.karlsson': ROOMS[6].number, // Doppio
};

export const mockDevices = NAMES.map((identity, index) => {
    const { roomNumber } = FLOORS[index];
    const location = MEETING_ROOM_OCCUPANT_LOCATIONS[identity] || roomNumber;

    return {
        assocTime: now - index * 37,
        identity,
        firstSeen: now - 60 * 60 * 24 * (10 + index),
        lastSeen: now - index * 5,
        hostname: `${identity.split('.')[0].toUpperCase()}-MBP`,
        mac: `00:11:22:33:44:${String(index + 1).padStart(2, '0')}`,
        isPersonal: true,
        locator: true,
        ap: { name: `AP-${location}`, location },
    };
});

const BOOKING_ORGANIZERS = NAMES;

export const mockBookingsToday = ROOMS.map((room, roomIndex) => {
    const slots = ROOM_BOOKINGS[room.name] || [];

    return {
        identifier: roomIdentifier(room),
        displayName: room.name,
        bookings: slots.map((slot, slotIndex) => {
            const [startHours, startMinutes, endHours, endMinutes] = slot;
            const organizer = BOOKING_ORGANIZERS[(roomIndex * 3 + slotIndex) % BOOKING_ORGANIZERS.length];

            return {
                start: timeToday(startHours, startMinutes),
                end: timeToday(endHours, endMinutes),
                organizer: `${organizer}@stendahls.se`,
                subject: '',
            };
        }),
    };
});
